import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from './http.service';

@Injectable({
  providedIn: 'root',
})
export class RegulatoryTransactionService {
  basePath = '/admin';

  REGULATORY_DECLARATION = `/regulatory-declarations`;
  REGULATORY_DROPDOWN = `/regulatory-dropdown`;
  FLOWS_TYPE = `/flows-journeys`;
  FILTERS_FLOWS = `${this.REGULATORY_DECLARATION}/filters/flows`;
  PUT_TRANSACTION_PARTY = `${this.REGULATORY_DECLARATION}/:id/parties`;
  PUT_TRANSACTION_TRANSPORT = `${this.REGULATORY_DECLARATION}/:id/transports`;
  PUT_TRANSACTION_CPC = `${this.REGULATORY_DECLARATION}/:id/cpc`;
  PUT_TRANSACTION_DOCUMENT = `${this.REGULATORY_DECLARATION}/:id/documents`;
  PUT_TRANSACTION_CARGO = `${this.REGULATORY_DECLARATION}/:id/cargos`;
  INVOICE = `${this.REGULATORY_DECLARATION}/:id/invoices`;
  ITEM = `${this.REGULATORY_DECLARATION}/:id/items`;
  SUBMISSION = `${this.REGULATORY_DECLARATION}/:id/submissions`;
  CLONES = `${this.REGULATORY_DECLARATION}/:id/clones`;

  constructor(private http: HttpService) {}

  getTransactionList(params: object = {}): Observable<any> {
    return this.http.get(`${this.REGULATORY_DECLARATION}`, params);
  }

  getAdminTransactionList(params: object = {}): Observable<any> {
    return this.http.get(
      `${this.basePath}${this.REGULATORY_DECLARATION}`,
      params
    );
  }

  getFlows(params: object = {}, ignoreLoading = false): Observable<any> {
    const hideLoadingBar = ignoreLoading
      ? { headers: { ignoreLoadingBar: '' } }
      : null;
    return this.http.get(`${this.FLOWS_TYPE}`, params, hideLoadingBar);
  }

  getFiltersFlows(params: object = {}, ignoreLoading = false): Observable<any> {
    const hideLoadingBar = ignoreLoading
      ? { headers: { ignoreLoadingBar: '' } }
      : null;
    return this.http.get(`${this.FILTERS_FLOWS}`, params, hideLoadingBar);
  }

  getFlowByID(id: number): Observable<any> {
    return this.http.get(`${this.FLOWS_TYPE}/${id}`);
  }

  getInvoices(params: object = {}, id, admin?): Observable<any> {
    return this.http.get(
      `${admin ? this.basePath : ''}${this.INVOICE.replace(':id', id)}`,
      params
    );
  }

  getItems(params: object = {}, id, admin?): Observable<any> {
    return this.http.get(
      `${admin ? this.basePath : ''}${this.ITEM.replace(':id', id)}`,
      params
    );
  }

  getSubmission(params: object = {}, id, admin?): Observable<any> {
    return this.http.get(
      `${admin ? this.basePath : ''}${this.SUBMISSION.replace(':id', id)}`,
      params
    );
  }

  deleteTransaction(id: number): Observable<any> {
    return this.http.delete(`${this.REGULATORY_DECLARATION}/${id}`);
  }

  getTransactionDetail(id, admin?): Observable<any> {
    return this.http.get(
      `${admin ? this.basePath : ''}${this.REGULATORY_DECLARATION}/${id}`
    );
  }

  getRegulatoryDropdown(params: object = {}): Observable<any> {
    return this.http.get(`${this.REGULATORY_DROPDOWN}`, params);
  }

  saveTransAsDraft(body: any = {}): Observable<any> {
    let { method, ...data } = body;
    method = (method as string).split('_');
    return this.http[method[0]](
      `${this.REGULATORY_DECLARATION}/${method[1] || ''}`,
      data
    );
  }

  updatePartyForm(id, params: object = {}): Observable<any> {
    return this.http.put(
      `${this.PUT_TRANSACTION_PARTY.replace(':id', id)}`,
      params
    );
  }

  updateCargoForm(id, params: object = {}): Observable<any> {
    return this.http.put(
      `${this.PUT_TRANSACTION_CARGO.replace(':id', id)}`,
      params
    );
  }

  updateCPCForm(id, params: object = {}): Observable<any> {
    return this.http.put(
      `${this.PUT_TRANSACTION_CPC.replace(':id', id)}`,
      params
    );
  }

  updateDocsForm(id, params: object = {}): Observable<any> {
    return this.http.put(
      `${this.PUT_TRANSACTION_DOCUMENT.replace(':id', id)}`,
      params
    );
  }

  updateTransportForm(id, params: object = {}): Observable<any> {
    return this.http.put(
      `${this.PUT_TRANSACTION_TRANSPORT.replace(':id', id)}`,
      params
    );
  }

  createInvoice(body: object = {}, id): Observable<any> {
    return this.http.post(`${this.INVOICE.replace(':id', id)}`, body);
  }

  updateInvoice(body: object = {}, id, invoiceID): Observable<any> {
    return this.http.patch(
      `${this.INVOICE.replace(':id', id)}/${invoiceID}`,
      body
    );
  }

  deleteInvoice(transactionId: number, invoiceID: number): Observable<any> {
    return this.http.delete(
      `${this.REGULATORY_DECLARATION}/${transactionId}/invoices/${invoiceID}`
    );
  }

  createItem(body: object = {}, id): Observable<any> {
    return this.http.put(`/regulatory-declarations/${id}/items`, body);
  }

  updateItem(body: object = {}, id): Observable<any> {
    return this.http.put(`/regulatory-declarations/${id}/items`, body);
  }

  deleteItem(transactionId: number, itemId: number): Observable<any> {
    return this.http.delete(
      `${this.REGULATORY_DECLARATION}/${transactionId}/items/${itemId}`
    );
  }

  getStatistic(): Observable<any> {
    return this.http.get(`${this.REGULATORY_DECLARATION}/dashboard`);
  }
  getAdminStatistic(params: object = {}): Observable<any> {
    return this.http.get(
      `${this.basePath}${this.REGULATORY_DECLARATION}/dashboard`,
      params
    );
  }

  saveSummaryAsDraft(body, id): Observable<any> {
    return this.http.patch(`${this.REGULATORY_DECLARATION}/${id}`, body);
  }

  submitDeclaration(id): Observable<any> {
    return this.http.post(`${this.REGULATORY_DECLARATION}/${id}/submit`, {
      dec_stmt_ind: true,
    });
  }

  getExistedDeclaration(body): Observable<any> {
    return this.http.get(`${this.REGULATORY_DECLARATION}/parties`, {
      offset: 0,
      limit: 100,
      where: body,
    });
  }

  cloneDeclaration(params: object = {}, id): Observable<any> {
    return this.http.post(`${this.CLONES.replace(':id', id)}`, params);
  }
}
